import React, { useState, useRef } from 'react';
import { Upload, Loader2, FileVideo, X, AlertCircle, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface VideoUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (data: {
    file: File;
    title?: string;
    customInstructions?: string;
    metadataInstructions?: string;
    playlistPolicy: 'mixable' | 'standalone';
    playlistGroup?: string;
  }) => Promise<void>;
  isUploading?: boolean;
}

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB
const MAX_METADATA_INSTRUCTIONS_LENGTH = 2000;

export const VideoUploadDialog: React.FC<VideoUploadDialogProps> = ({
  open,
  onOpenChange,
  onUpload,
  isUploading = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [metadataInstructions, setMetadataInstructions] = useState('');
  const [playlistPolicy, setPlaylistPolicy] = useState<'mixable' | 'standalone'>('mixable');
  const [playlistGroup, setPlaylistGroup] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setFile(null);
    setTitle('');
    setCustomInstructions('');
    setMetadataInstructions('');
    setPlaylistPolicy('mixable');
    setPlaylistGroup('');
    setError(null);
  };

  const handleClose = () => {
    if (!isUploading) {
      resetForm();
      onOpenChange(false);
    }
  };

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload MP4, MOV, AVI, or WebM files.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 1GB limit.';
    }
    return null;
  };

  const handleFileSelect = (selectedFile: File) => {
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setFile(selectedFile);
    // Auto-fill title from filename if empty
    if (!title) {
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
      setTitle(nameWithoutExt);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a video file.');
      return;
    }

    try {
      await onUpload({
        file,
        title: title.trim() || undefined,
        customInstructions: customInstructions.trim() || undefined,
        metadataInstructions: metadataInstructions.trim() || undefined,
        playlistPolicy,
        playlistGroup: playlistGroup.trim() || undefined,
      });
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Video
          </DialogTitle>
          <DialogDescription>
            Upload a video file. AI will automatically create segments and generate metadata.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* File Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
              transition-colors
              ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'}
              ${file ? 'bg-muted/50' : ''}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_VIDEO_TYPES.join(',')}
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) handleFileSelect(selectedFile);
              }}
              className="hidden"
            />

            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileVideo className="h-8 w-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium truncate max-w-[200px]">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">Drag & drop a video file here</p>
                <p className="text-xs text-muted-foreground mt-1">
                  or click to browse (MP4, MOV, AVI, WebM up to 1GB)
                </p>
              </>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title (optional)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter video title"
            />
          </div>

          <div className="rounded-lg border bg-card p-4 space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-medium">Playlist behavior</h3>
              <p className="text-xs text-muted-foreground">
                Control whether this demo can be mixed with other videos and, if so, which lane it belongs to.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="playlistPolicy">Mixing Policy</Label>
              <Select
                value={playlistPolicy}
                onValueChange={(value: 'mixable' | 'standalone') => setPlaylistPolicy(value)}
              >
                <SelectTrigger id="playlistPolicy">
                  <SelectValue placeholder="Select a policy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mixable">Mixable with matching videos</SelectItem>
                  <SelectItem value="standalone">Standalone only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Standalone videos will never be combined with segments from another upload.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="playlistGroup">Playlist Group (optional)</Label>
              <Input
                id="playlistGroup"
                value={playlistGroup}
                onChange={(e) => setPlaylistGroup(e.target.value)}
                placeholder="mobile, desktop, enterprise"
              />
              <p className="text-xs text-muted-foreground">
                Use the same group name for videos that are allowed to mix together. Leave blank if this video does not need a group.
              </p>
            </div>
          </div>

          {/* AI Processing Info */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              <span className="text-sm font-medium">AI-Powered Processing</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Your video will be automatically segmented and enriched with AI-generated metadata including labels, descriptions, and tags.
            </p>

            {/* Custom Instructions */}
            <div className="space-y-2 pt-2">
              <Label htmlFor="customInstructions" className="text-sm">
                Custom Instructions (optional)
              </Label>
              <Textarea
                id="customInstructions"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder="e.g., Focus on product features. Skip the intro section. Highlight pricing discussions."
                rows={2}
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Guide segmentation boundaries and what the AI should prioritize while splitting the video
              </p>
            </div>

            <div className="space-y-2 pt-2">
              <Label htmlFor="metadataInstructions" className="text-sm">
                Metadata Instructions (optional)
              </Label>
              <Textarea
                id="metadataInstructions"
                value={metadataInstructions}
                onChange={(e) => setMetadataInstructions(e.target.value.slice(0, MAX_METADATA_INSTRUCTIONS_LENGTH))}
                placeholder="e.g., Use customer-facing language, emphasize setup steps, and keep labels short and action-oriented."
                rows={3}
                maxLength={MAX_METADATA_INSTRUCTIONS_LENGTH}
                className="text-sm"
              />
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  Guide metadata wording, emphasis, and terminology without affecting segmentation boundaries
                </p>
                <span className="text-xs text-muted-foreground">
                  {metadataInstructions.length}/{MAX_METADATA_INSTRUCTIONS_LENGTH}
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!file || isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Video
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoUploadDialog;
